type CacheEntry = {
  body: any
  expiresAt: number
  headers: Record<string, string>
}

type CacheGlobals = typeof globalThis & {
  __edhrecCache?: Record<string, CacheEntry>
  __edhrecCacheCleanupInterval?: ReturnType<typeof setInterval>
}

const cacheGlobals = globalThis as CacheGlobals
const cache = cacheGlobals.__edhrecCache ?? (cacheGlobals.__edhrecCache = {})
const CACHE_CLEANUP_INTERVAL_MS = 60_000

const extractHeaders = (headers: Headers): Record<string, string> => {
  const result: Record<string, string> = {}
  headers.forEach((value, key) => {
    result[key] = value
  })
  return result
}

const parseMaxAge = (cacheControl: string | null): number => {
  if (!cacheControl) return 0

  const match = cacheControl.match(/max-age=(\d+)/)
  return match ? parseInt(match[1], 10) : 0
}

export const cachedFetch = async (url: string, options?: RequestInit): Promise<Response> => {
  const now = Date.now()
  const cached = cache[url]

  if (cached) {
    if (now < cached.expiresAt) {
      return new Response(JSON.stringify(cached.body), {
        headers: {
          ...cached.headers,
          'X-Cache': 'HIT',
        },
        status: 200,
      })
    } else {
      delete cache[url] // lazy cleanup
    }
  }

  const res = await fetch(url, options)

  if (!res.ok) {
    throw new Error(`Fetch failed: ${res.status} ${res.statusText}`)
  }

  const cacheControl = res.headers.get('Cache-Control')
  const maxAge = parseMaxAge(cacheControl)
  const ageHeader = res.headers.get('Age')
  const parsedAge = ageHeader ? parseInt(ageHeader, 10) : 0
  const age = Number.isFinite(parsedAge) && parsedAge >= 0 ? parsedAge : 0
  const remainingTtlSeconds = Math.max(0, maxAge - age)

  const body = await res.json()
  const headers = extractHeaders(res.headers)

  if (remainingTtlSeconds > 0) {
    // Jitter up to 10% of TTL (max 30s) to prevent synchronized cache expiry across pods
    // May or may not be helpful
    // https://discord.com/channels/752903319072276540/1232685062106124318/1489588137230598236
    const jitterMs = Math.floor(Math.random() * Math.min(remainingTtlSeconds * 100, 30_000))
    cache[url] = {
      body,
      expiresAt: now + remainingTtlSeconds * 1000 + jitterMs,
      headers,
    }
  }

  return new Response(JSON.stringify(body), {
    headers: {
      ...headers,
      'X-Cache': 'MISS',
    },
    status: res.status,
  })
}

const cleanupExpiredCacheEntries = () => {
  const now = Date.now()
  for (const [url, entry] of Object.entries(cache)) {
    if (now >= entry.expiresAt) {
      delete cache[url]
    }
  }
}

if (!cacheGlobals.__edhrecCacheCleanupInterval) {
  cacheGlobals.__edhrecCacheCleanupInterval = setInterval(cleanupExpiredCacheEntries, CACHE_CLEANUP_INTERVAL_MS)
}
